import React, { useState } from 'react';

const FAILURE_PATTERNS = [
  'failed',
  'failed to',
  'failed to post job on',
  'timeout',
  'unable',
  'exception',
  'blocked',
  'payment_required',
  'free_job_limit_reached',
  'active free job limit reached',
  "don't have access",
  'do not have access',
  'limit reached',
  'job posting failed',
  'job post failed',
  'could not be completed',
  'could not complete the posting process',
  'could not find the publish controls',
  'try reposting',
];
const NON_FAILURE_PATTERNS = [
  'will retry if error occurs',
  'retry if error occurs',
  'sent free job limit error to chat',
];
const SUCCESS_PATTERNS = [
  'posted successfully',
  'job posted successfully',
  'successfully published',
  'job is live',
  'your job is live',
  'completed successfully',
  'finished successfully',
  'candidate sourcing completed',
  'resume box matching complete',
  'no candidates with email found',
  'no matching candidates found',
  '0 candidates found',
  '0 profiles found',
  'email sync completed',
  'sync complete',
];
const INTERMEDIATE_SUCCESS_PATTERNS = [
  'login success',
  'login successful',
  'logged in successfully',
  'already logged in',
  'otp verification success',
  'verification successful',
  'loaded saved session successfully',
  'session saved successfully',
];
const TRANSIENT_PROGRESS_PATTERNS = [
  'waiting for page to load',
  'waiting for page to fully load',
  'waiting and checking again',
  'still on same step',
  'step mismatch',
  'detected step',
  'clicking next button',
  'step before clicking next',
  'step after clicking next',
  'no errors found but still on same step',
  'attempt ',
];
const CONTEXT_ONLY_PATTERNS = [
  'screenshot saved',
  'error screenshot saved',
  'page url:',
  'page title:',
  'browser context closed',
  'browser closed',
  'playwright stopped',
  'closing browser',
  'closing playwright',
  'saved session successfully',
  'session saved successfully',
  'saved ',
  'saving session',
  'saving naukri session',
  'saving linkedin session',
  'persisting session',
  'loaded saved session successfully',
];
const FINAL_FAILURE_PATTERNS = [
  'failed to post job on',
  'sending chat status message: job posting failed',
  'job posting failed:',
  'job posting failed -',
  'could not be completed',
  'could not complete the posting process',
  'could not find the publish controls',
  'try reposting',
  'browser context closed',
  'browser closed',
  'playwright stopped',
  'window closed',
  'target page, context or browser has been closed',
  'page closed',
  'no response from naukri',
  'no response from linkedin',
  'timed out waiting for response',
  'timed out after',
];
const RUN_START_PATTERNS = [
  'starting new browser instance',
  'starting linkedin login process',
  'starting naukri login process',
  'starting job posting process',
  'browser started successfully',
  'navigating to job posting page',
  'launching browser',
];
const LATEST_RUN_GAP_MS = 15 * 60 * 1000;

const SCREENSHOT_ARTIFACT_PATTERN = /(?:error\s+)?screenshot(?:\s+saved)?:\s*(?:.*\/)?([^\s/]+\.(?:png|jpg|jpeg))/i;
const FAILURE_ARTIFACT_PATTERNS = [
  'failed',
  'failure',
  'error',
  'not_found',
  'timeout',
  'timed_out',
  'blocked',
  'payment',
];
const SUCCESS_ARTIFACT_PATTERNS = [
  'success',
  'posted',
  'completed',
];

const getScreenshotArtifactFilename = (content = '') =>
  content.match(SCREENSHOT_ARTIFACT_PATTERN)?.[1]?.toLowerCase() || '';

const hasFailureArtifactSignal = (content = '') => {
  const filename = getScreenshotArtifactFilename(content);
  return !!(
    filename &&
    FAILURE_ARTIFACT_PATTERNS.some((pattern) => filename.includes(pattern))
  );
};

const hasSuccessArtifactSignal = (content = '') => {
  const filename = getScreenshotArtifactFilename(content);
  return !!(
    filename &&
    SUCCESS_ARTIFACT_PATTERNS.some((pattern) => filename.includes(pattern))
  );
};

export const cleanLogMessage = (message = '') =>
  String(message)
    .replace(/^[^\w(]+/u, '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeLogEntries = (logs = []) =>
  (Array.isArray(logs) ? logs : [])
    .map((log, index) => {
      const isPrimitiveLog = typeof log === 'string' || typeof log === 'number';
      const message = cleanLogMessage(
        isPrimitiveLog
          ? String(log)
          : (log?.message || log?.logMessage || '')
      );
      const status = String(isPrimitiveLog ? '' : (log?.status || log?.logType || log?.type || '')).toLowerCase();
      const timestamp = isPrimitiveLog ? null : (log?.timestamp || log?.created_at || log?.rawTimestamp || null);
      return {
        index,
        message,
        status,
        timestamp,
        raw: log,
      };
    })
    .filter((log) => log.message)
    .sort((left, right) => {
      const leftDate = toValidDate(left.timestamp);
      const rightDate = toValidDate(right.timestamp);

      if (leftDate && rightDate) {
        return leftDate.getTime() - rightDate.getTime();
      }

      if (leftDate) return -1;
      if (rightDate) return 1;
      return left.index - right.index;
    });

const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLogTime = (value) => {
  const date = toValidDate(value);
  if (!date) return '';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const isFailureLog = (log) => {
  const content = `${log.status} ${log.message}`.toLowerCase();
  if (isContextOnlyLog(log)) return false;
  if (log.status === 'error' || log.status === 'failed') return true;
  if (hasFailureArtifactSignal(content)) {
    return true;
  }
  if (NON_FAILURE_PATTERNS.some((pattern) => content.includes(pattern))) return false;
  if (/\berror\b/.test(content)) return true;
  return FAILURE_PATTERNS.some((pattern) => content.includes(pattern));
};

const isSuccessLog = (log) => {
  const content = `${log.status} ${log.message}`.toLowerCase();
  if (hasSuccessArtifactSignal(content)) {
    return true;
  }
  if (INTERMEDIATE_SUCCESS_PATTERNS.some((pattern) => content.includes(pattern))) {
    return false;
  }

  return (
    log.status === 'completed' ||
    SUCCESS_PATTERNS.some((pattern) => content.includes(pattern))
  );
};

const isTransientProgressLog = (log) => {
  const content = `${log.status} ${log.message}`.toLowerCase();
  return TRANSIENT_PROGRESS_PATTERNS.some((pattern) => content.includes(pattern));
};

const isContextOnlyLog = (log) => {
  const content = `${log.status} ${log.message}`.toLowerCase();
  if (hasFailureArtifactSignal(content) || hasSuccessArtifactSignal(content)) {
    return false;
  }
  return CONTEXT_ONLY_PATTERNS.some((pattern) => content.includes(pattern));
};

const isRunStartLog = (log) => {
  const content = `${log.status} ${log.message}`.toLowerCase();
  return RUN_START_PATTERNS.some((pattern) => content.includes(pattern));
};

const formatActiveSummary = (message = '') => {
  const text = cleanLogMessage(message)
    .replace(/^(Step\s+\d+(?:\.\d+)?:\s*)/i, '')
    .replace(/^(Reach Screen\s+\d+:\s*)/i, '')
    .replace(/^Typing location:\s*[^()]*\(full:\s*([^)]+)\)/i, 'Filling location: $1')
    .replace(/^Typing job title character-by-character:\s*/i, 'Filling job title: ')
    .replace(/^Typing min salary:\s*/i, 'Filling minimum salary: ')
    .replace(/^Typing max salary:\s*/i, 'Filling maximum salary: ')
    .replace(/^Handling ['"]Allow candidates to call you['"]\.\.\./i, 'Setting communication preferences')
    .trim();

  return text || 'The process is running.';
};

const deriveActiveLabel = (message = '') => {
  const text = message.toLowerCase();
  if (!text) return 'In progress';
  if (text.includes('location')) return 'Filling location';
  if (text.includes('job title') || text.includes('title')) return 'Filling job title';
  if (text.includes('salary')) return 'Filling salary';
  if (text.includes('skill')) return 'Adding skills';
  if (text.includes('communication preference') || text.includes('allow candidates to call')) return 'Setting communication';
  if (text.includes('screening')) return 'Screening questions';
  if (text.includes('description')) return 'Preparing description';
  if (text.includes('browser') || text.includes('navigating')) return 'Opening platform';
  if (text.includes('login')) return 'Logging in';
  if (text.includes('search')) return 'Searching';
  if (text.includes('review')) return 'Reviewing';
  if (text.includes('scan')) return 'Scanning';
  if (text.includes('filter')) return 'Filtering';
  if (text.includes('enrich')) return 'Enriching';
  if (text.includes('upload')) return 'Uploading';
  if (text.includes('parse')) return 'Parsing';
  if (text.includes('match')) return 'Matching';
  if (text.includes('post') || text.includes('publish')) return 'Posting';
  if (text.includes('collect')) return 'Collecting';
  if (text.includes('sync')) return 'Syncing';
  return 'In progress';
};

const getActiveLogEntry = (entries = []) =>
  [...entries].reverse().find(
    (entry) => !isFailureLog(entry) && !isTransientProgressLog(entry) && !isContextOnlyLog(entry),
  ) ||
  [...entries].reverse().find((entry) => !isFailureLog(entry) && !isTransientProgressLog(entry)) ||
  entries[entries.length - 1] ||
  null;

const getLatestRunEntries = (entries = []) => {
  const lastStartIndex = [...entries].reverse().findIndex(isRunStartLog);
  if (lastStartIndex !== -1) {
    const normalizedStartIndex = entries.length - 1 - lastStartIndex;
    return entries.slice(normalizedStartIndex);
  }

  for (let index = entries.length - 1; index > 0; index -= 1) {
    const currentTime = toValidDate(entries[index]?.timestamp);
    const previousTime = toValidDate(entries[index - 1]?.timestamp);

    if (!currentTime || !previousTime) continue;

    if ((currentTime.getTime() - previousTime.getTime()) > LATEST_RUN_GAP_MS) {
      return entries.slice(index);
    }
  }

  return entries;
};

const getDisplayLogEntry = (entries = []) =>
  [...entries].reverse().find((entry) => !isContextOnlyLog(entry)) ||
  entries[entries.length - 1] ||
  null;

const hasProgressAfterIndex = (entries = [], index = -1) =>
  entries.some(
    (entry) =>
      entry.index > index &&
      !isContextOnlyLog(entry) &&
      !isTransientProgressLog(entry) &&
      !isFailureLog(entry) &&
      !isSuccessLog(entry),
  );

const hasFinalFailureSignal = (entries = [], failureEntry = null) => {
  if (!failureEntry) return false;
  const failureIndex = failureEntry.index;
  return entries.some((entry) => {
    if (entry.index < failureIndex) return false;
    const content = `${entry.status} ${entry.message}`.toLowerCase();
    if (hasFailureArtifactSignal(content)) return true;
    return FINAL_FAILURE_PATTERNS.some((pattern) => content.includes(pattern));
  });
};

const getProcessWindow = (entries = []) => {
  const runEntries = getLatestRunEntries(entries);
  const firstTimedEntry = runEntries.find((entry) => toValidDate(entry.timestamp));
  const lastTerminalEntry = [...runEntries].reverse().find((entry) => (isFailureLog(entry) || isSuccessLog(entry)) && toValidDate(entry.timestamp));
  const latestTimedEntry = [...runEntries].reverse().find((entry) => toValidDate(entry.timestamp));

  return {
    startedAt: firstTimedEntry?.timestamp || null,
    completedAt: lastTerminalEntry?.timestamp || null,
    lastUpdatedAt: latestTimedEntry?.timestamp || null,
  };
};

export const getLatestRunState = (logs = []) => {
  const entries = normalizeLogEntries(logs);
  const windowEntries = getLatestRunEntries(entries);
  const lastFailure = [...windowEntries].reverse().find((entry) => isFailureLog(entry) && !isContextOnlyLog(entry)) || null;
  const lastSuccess = [...windowEntries].reverse().find(isSuccessLog) || null;
  const latestTerminalIsSuccess = lastSuccess && (!lastFailure || lastSuccess.index > lastFailure.index);
  const latestTerminalEntry =
    latestTerminalIsSuccess
      ? lastSuccess
      : lastFailure && (!lastSuccess || lastFailure.index > lastSuccess.index)
      ? lastFailure
      : null;
  const failureShouldWin =
    !!(
      lastFailure &&
      hasFinalFailureSignal(windowEntries, lastFailure)
    );
  const successShouldWin =
    !!(!failureShouldWin &&
      latestTerminalIsSuccess &&
      latestTerminalEntry &&
      !hasProgressAfterIndex(windowEntries, latestTerminalEntry.index));
  const terminalShouldWin = successShouldWin || failureShouldWin;
  const hasNonTerminalProgress = windowEntries.some(
    (entry) =>
      !isContextOnlyLog(entry) &&
      !isTransientProgressLog(entry) &&
      !isFailureLog(entry) &&
      !isSuccessLog(entry),
  );

  return {
    entries,
    windowEntries,
    lastFailure,
    lastSuccess,
    latestTerminalIsSuccess,
    latestTerminalEntry,
    successShouldWin,
    failureShouldWin,
    terminalShouldWin,
    hasNonTerminalProgress,
    hasRunStarted: windowEntries.length > 0,
  };
};

export const getProcessInsight = (
  logs = [],
  processing = false,
  {
    idleLabel = 'Waiting',
    completedLabel = 'Completed',
    emptySummary = 'No activity yet.',
  } = {},
) => {
  const {
    entries,
    windowEntries,
    lastFailure,
    lastSuccess,
    latestTerminalIsSuccess,
    successShouldWin,
    failureShouldWin,
  } = getLatestRunState(logs);
  const lastEntry = windowEntries[windowEntries.length - 1] || entries[entries.length - 1] || null;
  const displayEntry = getDisplayLogEntry(windowEntries);
  const activeEntry = getActiveLogEntry(windowEntries);

  if (processing && !failureShouldWin) {
    return {
      tone: 'active',
      label: deriveActiveLabel(activeEntry?.message || lastEntry?.message),
      summary: formatActiveSummary(activeEntry?.message || displayEntry?.message || lastEntry?.message || 'The process is running.'),
      reason: null,
      entries,
      windowEntries,
      hasLogs: entries.length > 0,
    };
  }

  if (windowEntries.length > 0 && !successShouldWin && !failureShouldWin) {
    return {
      tone: 'active',
      label: deriveActiveLabel(activeEntry?.message || lastEntry?.message),
      summary: formatActiveSummary(activeEntry?.message || displayEntry?.message || lastEntry?.message || 'The process is running.'),
      reason: null,
      entries,
      windowEntries,
      hasLogs: true,
    };
  }

  if (latestTerminalIsSuccess && successShouldWin) {
    return {
      tone: 'success',
      label: completedLabel,
      summary: lastSuccess.message || displayEntry?.message || 'Process completed successfully.',
      reason: null,
      entries,
      windowEntries,
      hasLogs: entries.length > 0,
    };
  }

  if (lastFailure && failureShouldWin) {
    return {
      tone: 'danger',
      label: 'Failed',
      summary: lastFailure.message || displayEntry?.message,
      reason: lastFailure.message,
      entries,
      windowEntries,
      hasLogs: true,
    };
  }

  if (lastSuccess || entries.length > 0) {
    return {
      tone: 'success',
      label: completedLabel,
      summary: lastSuccess?.message || displayEntry?.message || 'Process completed successfully.',
      reason: null,
      entries,
      windowEntries,
      hasLogs: entries.length > 0,
    };
  }

  return {
    tone: 'idle',
    label: idleLabel,
    summary: emptySummary,
    reason: null,
    entries,
    windowEntries,
    hasLogs: false,
  };
};

const getToneStyles = (tone, isDark) => {
  switch (tone) {
    case 'active':
      return {
        accent: '#3B5BDB',
        pillBg: isDark ? 'rgba(59, 91, 219, 0.18)' : 'rgba(59, 91, 219, 0.12)',
        pillColor: '#3B5BDB',
        glow: isDark ? 'rgba(40, 172, 168, 0.14)' : 'rgba(40, 172, 168, 0.08)',
      };
    case 'success':
      return {
        accent: '#16A34A',
        pillBg: isDark ? 'rgba(22, 163, 74, 0.18)' : 'rgba(22, 163, 74, 0.12)',
        pillColor: '#15803D',
        glow: isDark ? 'rgba(22, 163, 74, 0.12)' : 'rgba(22, 163, 74, 0.06)',
      };
    case 'danger':
      return {
        accent: '#DC2626',
        pillBg: isDark ? 'rgba(220, 38, 38, 0.18)' : 'rgba(220, 38, 38, 0.1)',
        pillColor: '#DC2626',
        glow: isDark ? 'rgba(220, 38, 38, 0.12)' : 'rgba(220, 38, 38, 0.04)',
      };
    default:
      return {
        accent: '#64748B',
        pillBg: isDark ? 'rgba(100, 116, 139, 0.18)' : 'rgba(100, 116, 139, 0.1)',
        pillColor: isDark ? '#CBD5E1' : '#475569',
        glow: isDark ? 'rgba(100, 116, 139, 0.08)' : 'rgba(100, 116, 139, 0.04)',
      };
  }
};

const BRAND_GRADIENT = 'linear-gradient(135deg, #3b5bdb 0%, #28aca8 100%)';

const ExpandableText = ({
  text,
  isDark,
  collapsedLines = 3,
  textStyle = {},
  containerStyle = {},
}) => {
  const [expanded, setExpanded] = useState(false);
  const normalizedText = String(text || '').trim();

  if (!normalizedText) return null;

  const shouldCollapse = normalizedText.length > 140 || normalizedText.includes('\n');

  return (
    <div style={containerStyle}>
      <div
        title={normalizedText}
        style={{
          ...textStyle,
          ...(shouldCollapse && !expanded
            ? {
                display: '-webkit-box',
                WebkitLineClamp: collapsedLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }
            : null),
        }}
      >
        {normalizedText}
      </div>
      {shouldCollapse && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          style={{
            marginTop: 6,
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: isDark ? '#93C5FD' : '#1D4ED8',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

export const ProcessStatusCard = ({
  title,
  icon,
  metric,
  logs = [],
  processing = false,
  completedLabel = 'Completed',
  idleLabel = 'Waiting',
  emptySummary = 'No activity yet.',
  onViewLogs,
  actionLabel = '',
  onAction,
  actionDisabled = false,
  isDark = false,
  supplementaryContent = null,
  allowEmptyLogView = false,
  // statusOverride: when the parent already knows the status from DB/other authoritative source,
  // use this to force the card tone/label regardless of log-analysis heuristics.
  // 'success' | 'failed' | 'processing' | null
  statusOverride = null,
  summaryOverride = null,
  reasonOverride = null,
}) => {
  const rawInsight = getProcessInsight(logs, processing, {
    idleLabel,
    completedLabel,
    emptySummary,
  });
  // Apply status override: parent's DB-derived status wins over log-analysis heuristics
  const insight = statusOverride === 'success'
    ? { ...rawInsight, tone: 'success', label: completedLabel, reason: null }
    : statusOverride === 'failed'
    ? { ...rawInsight, tone: 'danger', label: 'Failed' }
    : statusOverride === 'processing'
    ? { ...rawInsight, tone: 'active', label: deriveActiveLabel(rawInsight.summary || metric), reason: null }
    : rawInsight;
  const displayedInsight = {
    ...insight,
    summary: summaryOverride || insight.summary,
    reason: reasonOverride || insight.reason,
  };
  const processWindow = getProcessWindow(displayedInsight.windowEntries || displayedInsight.entries);
  const toneStyles = getToneStyles(displayedInsight.tone, isDark);

  return (
    <div
      style={{
        borderRadius: 16,
        border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.5)' : 'rgba(177, 196, 232, 0.9)'}`,
        background: isDark
          ? `linear-gradient(180deg, rgba(15, 23, 42, 0.94) 0%, rgba(15, 23, 42, 0.88) 100%), ${toneStyles.glow}`
          : `linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(244,248,255,0.98) 100%)`,
        boxShadow: isDark
          ? `0 18px 36px ${toneStyles.glow}`
          : `0 10px 24px ${toneStyles.glow}, inset 0 1px 0 rgba(255,255,255,0.9)`,
        padding: '14px',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 160px' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: BRAND_GRADIENT,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 10px 20px rgba(59, 91, 219, 0.2)',
            }}
          >
            {icon}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 750, color: isDark ? '#F8FAFC' : '#0F172A', lineHeight: 1.25, wordBreak: 'normal', overflowWrap: 'normal' }}>{title}</div>
            <div style={{ fontSize: 12, color: isDark ? '#94A3B8' : '#64748B', marginTop: 2, lineHeight: 1.35 }}>
              {metric || `${displayedInsight.entries.length} log update${displayedInsight.entries.length === 1 ? '' : 's'}`}
            </div>
          </div>
        </div>
        <span
          style={{
            padding: '6px 10px',
            borderRadius: 999,
            background: toneStyles.pillBg,
            color: toneStyles.pillColor,
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          {displayedInsight.label}
        </span>
      </div>

      {displayedInsight.tone === 'active' && (
        <>
          <style>
            {`
              @keyframes processStatusSpin {
                to { transform: rotate(360deg); }
              }
              @keyframes processStatusPulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.45; transform: scale(0.82); }
              }
            `}
          </style>
          <div
            style={{
              marginTop: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              borderRadius: 18,
              background: isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(239, 246, 255, 0.96)',
              border: `1px solid ${isDark ? 'rgba(59, 91, 219, 0.45)' : 'rgba(147, 197, 253, 0.9)'}`,
              color: isDark ? '#DBEAFE' : '#1D4ED8',
              fontSize: 11,
              fontWeight: 800,
              maxWidth: '100%',
              flexWrap: 'wrap',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#28ACA8',
                animation: 'processStatusPulse 1s ease-in-out infinite',
              }}
            />
            <span style={{ whiteSpace: 'nowrap' }}>Processing</span>
            <span
              aria-hidden="true"
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                border: '2px solid rgba(59, 91, 219, 0.22)',
                borderTopColor: '#3B5BDB',
                animation: 'processStatusSpin 0.95s linear infinite',
              }}
            />
            <span
              style={{
                width: 1,
                alignSelf: 'stretch',
                minHeight: 14,
                background: isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(59, 91, 219, 0.18)',
              }}
            />
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.4,
                color: isDark ? '#E2E8F0' : '#1E293B',
                overflowWrap: 'anywhere',
                flex: '1 1 180px',
              }}
            >
              <ExpandableText
                text={displayedInsight.summary}
                isDark={isDark}
                collapsedLines={2}
                textStyle={{ lineHeight: 1.4, color: isDark ? '#E2E8F0' : '#1E293B' }}
              />
            </div>
          </div>
        </>
      )}

      {displayedInsight.tone !== 'active' && (
        <div
          style={{
            marginTop: 12,
            fontSize: 13,
            lineHeight: 1.5,
            color: isDark ? '#E2E8F0' : '#334155',
            overflowWrap: 'anywhere',
          }}
        >
          <ExpandableText
            text={displayedInsight.summary}
            isDark={isDark}
            collapsedLines={3}
            textStyle={{ lineHeight: 1.5, color: isDark ? '#E2E8F0' : '#334155' }}
          />
        </div>
      )}

      {displayedInsight.reason && (
        <div
          style={{
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 12,
            background: isDark ? 'rgba(127, 29, 29, 0.28)' : 'rgba(254, 242, 242, 0.95)',
            border: `1px solid ${isDark ? 'rgba(248, 113, 113, 0.24)' : 'rgba(252, 165, 165, 0.9)'}`,
            color: isDark ? '#FECACA' : '#991B1B',
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Reason:</div>
          <ExpandableText
            text={displayedInsight.reason}
            isDark={isDark}
            collapsedLines={3}
            textStyle={{ lineHeight: 1.5, color: isDark ? '#FECACA' : '#991B1B' }}
          />
        </div>
      )}

      {supplementaryContent && (
        <div style={{ marginTop: 10 }}>
          {supplementaryContent}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: isDark ? '#94A3B8' : '#64748B', flex: '1 1 220px', lineHeight: 1.5 }}>
          {processWindow.startedAt ? `Started ${formatLogTime(processWindow.startedAt)}` : 'Last update stays visible after completion.'}
          {processing && processWindow.lastUpdatedAt ? ` • Updated ${formatLogTime(processWindow.lastUpdatedAt)}` : ''}
          {!processing && processWindow.completedAt ? ` • Completed ${formatLogTime(processWindow.completedAt)}` : ''}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {actionLabel && onAction && (
            <button
              type="button"
              onClick={onAction}
              disabled={actionDisabled}
              style={{
                border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.8)' : 'rgba(177, 196, 232, 0.95)'}`,
                borderRadius: 999,
                padding: '7px 11px',
                background: actionDisabled
                  ? (isDark ? 'rgba(51, 65, 85, 0.45)' : 'rgba(248, 250, 252, 0.85)')
                  : 'transparent',
                color: actionDisabled ? (isDark ? '#94A3B8' : '#94A3B8') : (isDark ? '#E2E8F0' : '#334155'),
                fontSize: 12,
                fontWeight: 700,
                cursor: actionDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              {actionLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onViewLogs}
            disabled={!allowEmptyLogView && insight.entries.length === 0}
            style={{
              border: 'none',
              borderRadius: 999,
              padding: '7px 11px',
              background: !allowEmptyLogView && insight.entries.length === 0
                ? (isDark ? 'rgba(51, 65, 85, 0.5)' : 'rgba(226, 232, 240, 0.8)')
                : BRAND_GRADIENT,
              color: !allowEmptyLogView && insight.entries.length === 0 ? (isDark ? '#94A3B8' : '#94A3B8') : '#fff',
              fontSize: 12,
              fontWeight: 700,
              cursor: !allowEmptyLogView && insight.entries.length === 0 ? 'not-allowed' : 'pointer',
              boxShadow: !allowEmptyLogView && insight.entries.length === 0 ? 'none' : '0 12px 24px rgba(59, 91, 219, 0.2)',
            }}
          >
            View full log
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProcessLogsModal = ({
  open,
  title,
  logs = [],
  isDark = false,
  onClose,
  children,
}) => {
  if (!open) return null;

  const entries = normalizeLogEntries(logs);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.52)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(760px, 100%)',
          maxHeight: 'min(78vh, 900px)',
          overflow: 'hidden',
          borderRadius: 24,
          border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.6)' : 'rgba(203, 213, 225, 0.9)'}`,
          background: isDark
            ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.92) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.99) 100%)',
          boxShadow: '0 30px 80px rgba(15, 23, 42, 0.28)',
        }}
      >
        <div
          style={{
            padding: '18px 20px',
            borderBottom: `1px solid ${isDark ? 'rgba(51, 65, 85, 0.8)' : 'rgba(226, 232, 240, 1)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: isDark ? '#F8FAFC' : '#0F172A' }}>{title}</div>
            <div style={{ fontSize: 12, color: isDark ? '#94A3B8' : '#64748B', marginTop: 4 }}>
              {entries.length} log entr{entries.length === 1 ? 'y' : 'ies'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.8)' : 'rgba(203, 213, 225, 1)'}`,
              background: 'transparent',
              color: isDark ? '#CBD5E1' : '#475569',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 20, maxHeight: 'calc(78vh - 82px)', overflowY: 'auto' }}>
          {children}
          {children && entries.length > 0 && (
            <div
              style={{
                margin: '18px 0 8px',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: isDark ? '#94A3B8' : '#64748B',
              }}
            >
              Full timeline
            </div>
          )}
          {entries.length === 0 ? (
            <div style={{ padding: '28px 12px', textAlign: 'center', color: isDark ? '#94A3B8' : '#64748B' }}>
              No logs available yet.
            </div>
          ) : (
            entries.map((log) => {
              const tone = isFailureLog(log) ? '#DC2626' : isSuccessLog(log) ? '#16A34A' : '#3B5BDB';
              return (
                <div
                  key={`${log.index}-${log.message}`}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '11px 0',
                    borderBottom: `1px solid ${isDark ? 'rgba(51, 65, 85, 0.55)' : 'rgba(241, 245, 249, 1)'}`,
                  }}
                >
                  <span style={{ color: tone, fontSize: 12, lineHeight: '20px' }}>●</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, lineHeight: 1.6, color: isDark ? '#E2E8F0' : '#334155' }}>
                      {log.message}
                    </div>
                    {log.timestamp && (
                      <div style={{ marginTop: 4, fontSize: 11, color: isDark ? '#94A3B8' : '#64748B' }}>
                        {formatLogTime(log.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
